import { useAppDispatch, useAppSelector } from 'store/hooks';
import { perkApi } from 'components/loyalty/PerkApi';
import { Perk } from 'components/loyalty/models/PerkModel';
import {
    MESSAGE_MARKETING_PERK_RESET_SUCCESS,
    MESSAGE_MARKETING_PERK_RESET_ERROR,
    MESSAGE_MARKETING_PERK_DELETE_ERROR,
    MESSAGE_MARKETING_PERK_DELETE_SUCCESS
} from 'config/messages';
import logger from 'lib/logger';
import React from 'react';
import { resetCache } from 'store/match-codes/matchCodesSlice';
import { useSnackbar } from 'notistack';
import {
    createSuccess,
    createSuccessClub,
    updateSuccess,
    startResetRequest,
    resetSuccess,
    resetError,
    startDeleteRequest,
    deleteSuccess,
    deleteError
} from 'store/perks/perksSlice';
export const usePepperPerksMutation = () => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const { perks, indexToReset, indexToDelete } = useAppSelector(state => state.perks);

    const handleCreateSuccess = React.useCallback(
        ({ perk, stepFour }: { perk: Perk; stepFour?: boolean }) => {
            if (stepFour) {
                return dispatch(createSuccessClub({ perk }));
            }
            dispatch(createSuccess({ perk }));
            dispatch(resetCache());
        },
        [dispatch]
    );

    const handleUpdateSuccess = React.useCallback(
        (perk: Perk) => {
            dispatch(updateSuccess({ perk }));
            dispatch(resetCache());
        },
        [dispatch]
    );

    const resetPerk = React.useCallback(async () => {
        try {
            dispatch(startResetRequest());

            const perk = perks[indexToReset];
            const result = await perkApi.reset(perk._id, {
                body: { confirmationString: 'RESET PERK' },
                skipResponseBody: true
            });
            if (!result.ok) {
                throw new Error(result.body.message);
            }

            dispatch(resetSuccess());
            enqueueSnackbar(MESSAGE_MARKETING_PERK_RESET_SUCCESS, { variant: 'success' });
            dispatch(resetCache());
        } catch (error) {
            logger.error(error.message);

            dispatch(resetError());
            enqueueSnackbar(MESSAGE_MARKETING_PERK_RESET_ERROR, { variant: 'error' });
        }
    }, [dispatch, perks, indexToReset, enqueueSnackbar]);

    const deletePerk = React.useCallback(async () => {
        try {
            dispatch(startDeleteRequest());
            const perk = perks[indexToDelete];
            const result = await perkApi.delete(perk._id, { skipResponseBody: true });
            if (!result.ok) {
                throw new Error(result.body.message);
            }
            dispatch(deleteSuccess());
            enqueueSnackbar(MESSAGE_MARKETING_PERK_DELETE_SUCCESS, { variant: 'success' });
            dispatch(resetCache());
        } catch (error) {
            logger.error(error.message);
            dispatch(deleteError());
            enqueueSnackbar(MESSAGE_MARKETING_PERK_DELETE_ERROR, { variant: 'error' });
        }
    }, [dispatch, perks, indexToDelete, enqueueSnackbar]);

    return {
        handleCreateSuccess,
        handleUpdateSuccess,
        resetPerk,
        deletePerk
    };
};
